// App header: kore TopBar with the product identity, the platform product
// switcher (platform's /v1/me/apps; static current-product fallback when the
// platform API is unconfigured), the user menu (GET /v1/me; hidden until identity
// resolves), the TopBar's own theme control, and sign-out via the BFF's
// RP-initiated logout URL. Ported from ewt's header; account-side theme
// persistence is deliberately not wired in v1 (theme stays local).

import { ProductSelector, TopBar } from "@pej/kore/components";
import type { Product, Theme, User, UserAction } from "@pej/kore/types";
import { Link } from "@tanstack/react-router";
import { useTheme } from "next-themes";
import { useMemo } from "react";
import { fetchSignOutUrl, signOutEnabled } from "@/api/accounts";
import { useIdentity } from "@/api/me";
import { useMeApps } from "@/api/platform";
import { env } from "@/lib/env";

export const PRODUCT = { shortName: "Surveys", abbreviation: "SRV", tone: "accent" as const };

function toKoreTheme(value: string | undefined): Theme {
    return value === "light" || value === "dark" || value === "system" ? value : "system";
}

export function Header() {
    const { theme, setTheme } = useTheme();
    const identity = useIdentity();
    const meApps = useMeApps();

    const me = identity.data;
    const user: User | undefined = me ? { displayName: me.user.name, email: me.user.email ?? "" } : undefined;

    const products: Product[] = useMemo(() => {
        const apps = meApps.data?.apps;
        if (apps && apps.length > 0) {
            return apps.map((app) => ({
                key: app.id,
                displayName: app.name,
                description: app.description,
                hue: app.hue,
                href: app.url,
            }));
        }
        // Platform API unconfigured, failed or not yet loaded: the grid trigger stays
        // clickable and the dropdown says "you're here".
        // key feeds kore's ProductBadge monogram (abbreviate(): first char +
        // three consonants, punctuation included).
        return [{ key: "surveys", displayName: "Surveys", current: true }];
    }, [meApps.data]);

    async function handleUserAction(action: UserAction) {
        if (action === "settings") {
            if (env.accountPortalUrl) window.location.assign(env.accountPortalUrl);
            return;
        }
        if (action === "signout") {
            if (!signOutEnabled()) return;
            // Top-level assign (not a link) so browser popup heuristics
            // don't open the SSO chain in a side panel.
            const href = await fetchSignOutUrl();
            if (href) window.location.assign(href);
        }
    }

    return (
        <TopBar
            product={PRODUCT}
            linkComponent={Link}
            productSelector={<ProductSelector products={products} linkComponent={Link} />}
            user={user}
            theme={toKoreTheme(theme)}
            onThemeChange={setTheme}
            onUserAction={handleUserAction}
        />
    );
}
